/**
 * @coeldery/session-list — **對話清單**（「之前嘅對話」）
 *
 * 為何係模組（R3；`pending_changes` **P-74**）：任何「多個對話／多個 session 嘅 AI app」都要
 *   同一個清單（名／預覽 ＋ 時間 ＋ 數量 ＋ 揀返入去 ＋ **改名／刪除**）→ 第二個 app 一定用得到。
 *
 * 血緣：cosingery（2026-09-22 產品負責人：「we should be able to see the chat history … on the top
 *   bar right hand side」；同日第四批：「the history should be able to **delete and edit** each session,
 *   giving it a **name of the conversation** to make sure they know how to find the history」）。
 *
 * ⚠️ 1.1.0：改名／刪除**只出意圖**（`onRename`／`onDelete`）—— 彈窗／確認／網絡請求由父層做
 *   （模組保持純 UI；亦唔會自己決定「刪咗會點」）。
 */
import { useTranslation } from 'react-i18next';

export interface SessionItem {
  /** 唯一 id（父層負責） */
  id: string;
  /** ⭐ 1.1.0：用戶改嘅對話名（冇就 fallback 去 `preview`） */
  title?: string | null;
  /** 預覽文字（例：最後一句訊息；可能係空） */
  preview?: string;
  /** 右邊／下面嘅細字（例：日期 · 3 首歌）—— 由父層砌好 */
  meta?: string;
}

export interface SessionListProps {
  items: SessionItem[];
  /** 而家喺邊個（會著燈） */
  currentId?: string;
  /** 載入中（照實顯示，唔准扮冇嘢） */
  busy?: boolean;
  /** 冇任何對話時嘅文字（i18n key；唔傳唔顯示） */
  emptyKey?: string;
  /** 預覽係空時嘅代替文字（i18n key） */
  untitledKey?: string;
  /** 載入中嘅文字（i18n key） */
  loadingKey?: string;
  /** ⭐ 1.1.0：改名意圖（唔傳 ＝ 唔顯示改名掣） */
  onRename?: (id: string, currentTitle: string) => void;
  /** ⭐ 1.1.0：刪除意圖（唔傳 ＝ 唔顯示刪除掣） */
  onDelete?: (id: string) => void;
  /** 進行中嘅 item（會停用掣，照實顯示） */
  busyId?: string | null;
  renameKey?: string;
  deleteKey?: string;
  onPick: (id: string) => void;
}

export default function SessionList({
  items,
  currentId,
  busy = false,
  emptyKey,
  untitledKey,
  loadingKey = 'chat_thinking',
  onRename,
  onDelete,
  busyId,
  renameKey = 'history_rename',
  deleteKey = 'history_delete',
  onPick,
}: SessionListProps) {
  const { t } = useTranslation();

  if (busy) return <p className="page__loading">{t(loadingKey)}</p>;
  if (items.length === 0) return emptyKey ? <p className="footnote">{t(emptyKey)}</p> : null;

  const iconBtn = (disabled: boolean): React.CSSProperties => ({
    minWidth: '36px',
    minHeight: '36px',
    borderRadius: '10px',
    border: '1.5px solid var(--color-divider)',
    background: 'var(--color-card)',
    color: 'var(--color-text)',
    fontFamily: 'inherit',
    fontSize: '15px',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  });

  return (
    <div data-testid="session-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {items.map((it) => {
        const active = it.id === currentId;
        const running = busyId === it.id;
        const label = it.title?.trim() || (it.preview?.trim() ?? '') || (untitledKey ? t(untitledKey) : '');
        return (
          <div key={it.id} style={{ display: 'flex', alignItems: 'stretch', gap: '6px' }}>
            <button
              type="button"
              data-testid="session-item"
              aria-current={active ? 'true' : undefined}
              onClick={() => onPick(it.id)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                flex: '1 1 auto',
                minWidth: 0,
                textAlign: 'left',
                padding: '10px 12px',
                border: '1.5px solid var(--color-divider)',
                borderRadius: '14px',
                background: active ? 'var(--color-primary-soft)' : 'var(--color-card)',
                color: 'var(--color-text)',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  /** 有名就當標題（粗體）；冇名就用最後訊息預覽 */
                  fontWeight: it.title?.trim() ? 700 : 400,
                  fontSize: 'var(--font-size-small, 15px)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
              {it.title?.trim() && it.preview?.trim() ? (
                <span className="footnote" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.preview}
                </span>
              ) : null}
              {it.meta ? <span className="footnote">{it.meta}</span> : null}
            </button>

            {onRename ? (
              <button
                type="button"
                data-testid="session-rename"
                aria-label={t(renameKey)}
                title={t(renameKey)}
                disabled={running}
                onClick={() => onRename(it.id, it.title ?? it.preview ?? '')}
                style={iconBtn(running)}
              >
                ✎
              </button>
            ) : null}
            {onDelete ? (
              <button
                type="button"
                data-testid="session-delete"
                aria-label={t(deleteKey)}
                title={t(deleteKey)}
                disabled={running}
                onClick={() => onDelete(it.id)}
                style={iconBtn(running)}
              >
                🗑
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
